import type { FavoriteItem } from '@/types/favorites';
import { useFavorites } from '@/ui/Layout/Favorites/FavoritesContext';
import { filterNavItems } from '@/ui/Layout/Sidebar/NavigationItems/filterNavItems';
import { baseNavigationItems } from '@/ui/Layout/Sidebar/NavigationItems/navigationItems';
import SidebarItem, { NavigationItem } from '@/ui/Layout/Sidebar/SidebarItem';
import useSession from '@/utils/hooks/useSession';
import { buildNavLabelTranslationKey } from '@/utils/i18n/navLabelKey';
import useHubTranslation from '@/utils/i18n/useHubTranslation';
import { StarRounded } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useCallback, useMemo, type ReactElement } from 'react';

interface SidebarItemsProps {
  collapsed: boolean;
  currentPath: string;
  handleItemClick: (item: NavigationItem) => void;
  onExpandSidebar?: () => void;
}

const SidebarItems = ({
  collapsed,
  currentPath,
  handleItemClick,
  onExpandSidebar,
}: SidebarItemsProps): ReactElement => {
  const { user, org } = useSession();
  const { favorites, isLoading: favoritesLoading } = useFavorites();
  const { translateHubUi } = useHubTranslation();

  const translateNavLabel = useCallback(
    (label: string) =>
      translateHubUi(buildNavLabelTranslationKey(label), label),
    [translateHubUi]
  );

  const translateNavItems = useCallback(
    (navItems: NavigationItem[]): NavigationItem[] =>
      navItems.map((item) => ({
        ...item,
        label: item.favorite ? item.label : translateNavLabel(item.label),
        items: item.items ? translateNavItems(item.items) : undefined,
      })),
    [translateNavLabel]
  );

  const favoriteItems = useMemo(() => {
    if (favoritesLoading) {
      return [
        {
          label: translateHubUi(
            'nav.favorites.loading',
            'Loading favorites...'
          ),
          disabled: true,
        },
      ] as NavigationItem[];
    }

    if (!favorites.length) {
      return [
        {
          label: translateHubUi(
            'nav.favorites.empty',
            'No Favorites added yet'
          ),
          disabled: true,
        },
      ] as NavigationItem[];
    }

    return favorites.map((favorite: FavoriteItem) => ({
      label: favorite.displayLabel,
      url: favorite.path,
      favorite: true,
    })) as NavigationItem[];
  }, [favorites, favoritesLoading, translateHubUi]);

  const items = useMemo(() => {
    if (!user || !org) return [];

    return [
      {
        label: translateHubUi('nav.favorites.myFavorites', 'My Favorites'),
        icon: {
          Icon: StarRounded,
        },
        isDrawer: true,
        items: favoriteItems,
      },
      ...translateNavItems(filterNavItems(user, org, baseNavigationItems)),
    ];
  }, [favoriteItems, org, translateHubUi, translateNavItems, user]);

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
      <Box component='ul' sx={{ m: 0, p: 0, listStyle: 'none' }}>
        {items.map(
          (item: NavigationItem, index: number, arr: NavigationItem[]) => (
            <SidebarItem
              key={`${item.label}-${index}`}
              item={item}
              depth={0}
              collapsed={collapsed}
              currentPath={currentPath}
              onItemClick={handleItemClick}
              isLastChild={index === arr.length - 1}
              onExpandSidebar={onExpandSidebar}
            />
          )
        )}
      </Box>
    </Box>
  );
};

export default SidebarItems;
